import React from "react";
import { Box, Card, Tooltip } from "@mui/joy";
import DarkTypography from "components/common/DarkTypography";
import IconButtonCTA from "components/common/IconButtonCTA";
import { SCreate } from "styles/StyledMUI";
import type {
  Excursion,
  ExcursionPlanningCardContext,
  ExcursionTemplateLimitsItem,
} from "api/excursions/Excursion.typed";
import { EXCURSION_CARD_FALLBACK_COLOR } from "utils/colorPalette";
import ExcursionCardContent from "./ExcursionCardContent";
import ExcursionPlanningCardPopover from "./ExcursionPlanningCardPopover";
import ExcursionDropPendingOverlay from "../../../shared/excursionModal/display/ExcursionDropPendingOverlay";
import { excursionCardGlassBorder } from "styles/globalStyles";
import {
  createPlanningExcursionCardClickHandler,
  usePlanningGroupsDrawerOpenSchedule,
} from "../hooks/usePlanningGroupsDrawerOpenSchedule";

export type ExcursionMonthDayContentProps = ExcursionPlanningCardContext & {
  day: Date;
  isToday: boolean;
  isWeekend: boolean;
  dayExcursions: Excursion[];
  onDayClick: (day: Date) => void;
  onExcursionDropOnDay: (day: Date, excursionId: number) => void;
  excursionTemplatesList?: ExcursionTemplateLimitsItem[];
};

export default function ExcursionMonthDayContent(props: ExcursionMonthDayContentProps) {
  const {
    day,
    isToday: _isToday,
    isWeekend: _isWeekend,
    dayExcursions,
    onDayClick,
    onExcursionDropOnDay,
    ...planningPopoverContext
  } = props;
  const {
    canEdit,
    canOpenExcursionDrawer,
    openPlanningGroupsDrawer,
    makeExcursionCardDragHandlers,
    makeExcursionCardDropHandlers,
    onDragEnterCard,
    onDragLeaveCard,
    excursionTemplatesList,
    chipDropPending,
    onOpenExcursionFullModal,
  } = planningPopoverContext;
  const { scheduleOpenDrawer, cancelScheduledDrawer } = usePlanningGroupsDrawerOpenSchedule();
  const handleDayDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("application/json");
    if (!raw || !canEdit) return;
    try {
      const d = JSON.parse(raw) as { type: string; excursionId?: number };
      if (d.type !== "excursion" || typeof d.excursionId !== "number") return;
      onExcursionDropOnDay(day, d.excursionId);
    } catch {
      /* ignore */
    }
  };

  return (
    <Box
      onDoubleClick={canEdit ? () => onDayClick(day) : undefined}
      onDragOver={
        canEdit
          ? (e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
            }
          : undefined
      }
      onDrop={canEdit ? handleDayDrop : undefined}
      sx={{
        width: "100%",
        cursor: canEdit || canOpenExcursionDrawer ? "pointer" : "default",
        minHeight: 28,
      }}
    >
      {(dayExcursions ?? []).slice(0, 5).map((ex) => {
        const cardMuted = ex.status === "cancelled" || ex.status === "validated";
        return (
          <ExcursionPlanningCardPopover key={ex.id} excursion={ex} {...planningPopoverContext}>
            <Card
              data-excursion-card
              size="sm"
              component="div"
              {...makeExcursionCardDragHandlers(ex)}
              {...makeExcursionCardDropHandlers(ex.id)}
              onClick={createPlanningExcursionCardClickHandler({
                canOpenExcursionDrawer,
                canEdit,
                onOpenExcursionFullModal,
                openPlanningGroupsDrawer,
                scheduleOpenDrawer,
                cancelScheduledDrawer,
                excursion: ex,
              })}
              onDragEnter={
                canEdit
                  ? (e: React.DragEvent) => {
                      e.stopPropagation();
                      onDragEnterCard(ex.id);
                    }
                  : undefined
              }
              onDragLeave={
                canEdit
                  ? (e: React.DragEvent) => {
                      e.stopPropagation();
                      onDragLeaveCard(ex.id, e);
                    }
                  : undefined
              }
              sx={{
                position: "relative",
                mb: { xs: 0.75, sm: 0.5 },
                backgroundColor: ex.color ?? EXCURSION_CARD_FALLBACK_COLOR,
                p: { xs: 0.5, sm: 0.5 },
                minHeight: "auto",
                border: excursionCardGlassBorder,
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                cursor: canEdit || canOpenExcursionDrawer ? "pointer" : "default",
                textAlign: "left",
                opacity: cardMuted ? 0.5 : 1,
                transition: "box-shadow 0.2s ease",
                boxShadow: "0 1px 6px rgba(0, 0, 0, 0.14)",
                "&:hover": { boxShadow: "0 3px 12px rgba(0, 0, 0, 0.2)" },
              }}
            >
              <ExcursionCardContent
                excursion={ex}
                layout="month"
                excursionTemplatesList={excursionTemplatesList}
              />
              <ExcursionDropPendingOverlay
                show={chipDropPending?.excursionId === ex.id && chipDropPending.surface === "card"}
              />
            </Card>
          </ExcursionPlanningCardPopover>
        );
      })}
      {(dayExcursions ?? []).length > 5 && (
        <DarkTypography level="body-xs" colorVariant="muted" sx={{ fontSize: "0.75rem" }}>
          +{(dayExcursions ?? []).length - 5} autres
        </DarkTypography>
      )}
      {canEdit && (
        <Tooltip title="Ajouter une sortie">
          <span>
            <IconButtonCTA
              size="sm"
              variant="soft"
              color="neutral"
              aria-label="Ajouter une sortie"
              sx={{ mt: 0.5, alignSelf: "flex-start" }}
              onClick={() => onDayClick(day)}
            >
              <SCreate />
            </IconButtonCTA>
          </span>
        </Tooltip>
      )}
    </Box>
  );
}
